import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchJWTToken, isTokenValid } from '../services/jwtTokenService';
import { log as logUtil } from '../utils/logger';

const MAX_RETRIES = 2;
// No auto-refresh - tokens are refreshed on-demand when API calls need them

const log = (message, data) => {
  logUtil(`[JWT Manager] ${message}`, data);
};

/**
 * Hook to manage JWT token lifecycle
 * Handles initialization, error states, and retry logic
 * Token refresh is on-demand only - no auto-refresh timers
 * @param {boolean} enableInitialization - Whether to enable JWT initialization (lazy load)
 */
export const useJWTTokenManager = (enableInitialization = false) => {
  const [isTokenInitialized, setIsTokenInitialized] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const retryCountRef = useRef(0);
  const isInitializingRef = useRef(false);

  /**
   * Calculate retry delay - simple fixed delays for 2 retries
   * @param {number} retryCount - Current retry attempt
   * @returns {number} Delay in milliseconds
   */
  const getRetryDelay = useCallback((retryCount) => {
    // First retry: 1 second, Second retry: 2 seconds
    return retryCount === 0 ? 1000 : 2000;
  }, []);

  /**
   * Initialize JWT token
   */
  const initializeToken = useCallback(async () => {
    if (isInitializingRef.current) {
      log('Token initialization already in progress');
      return;
    }

    isInitializingRef.current = true;

    try {
      log('Initializing JWT token', { retryCount: retryCountRef.current });

      await fetchJWTToken();

      setIsTokenInitialized(true);
      setTokenError(null);
      retryCountRef.current = 0;

      log('JWT token initialized successfully');
    } catch (error) {
      log('Failed to initialize JWT token', error);

      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const delay = getRetryDelay(retryCountRef.current - 1);

        log(`Retrying token initialization in ${delay}ms`, {
          attempt: retryCountRef.current,
          maxRetries: MAX_RETRIES,
        });

        setTimeout(() => {
          isInitializingRef.current = false;
          initializeToken();
        }, delay);
      } else {
        setTokenError(
          'Unable to initialize authentication. Please refresh the page to try again.'
        );
        log('Max retries reached. Token initialization failed.');
      }
    } finally {
      if (retryCountRef.current === 0 || retryCountRef.current >= MAX_RETRIES) {
        isInitializingRef.current = false;
      }
    }
  }, [getRetryDelay]);

  // No auto-refresh logic - tokens refresh on-demand when getValidJWTToken() is called

  // Initialize token when enableInitialization is true
  useEffect(() => {
    if (enableInitialization && !isTokenInitialized && !tokenError) {
      // Check if valid token already exists in cache
      if (isTokenValid()) {
        log('Valid token found in cache, skipping initialization');
        setIsTokenInitialized(true);
      } else {
        log('No valid token in cache, initializing new token');
        initializeToken();
      }
    }
  }, [enableInitialization, isTokenInitialized, tokenError, initializeToken]);

  return {
    isTokenInitialized,
    tokenError,
  };
};
